import Vue from 'vue'
import App from './App.vue'
import cleanBaseUrl from './utils/CleanBaseUrl.js'

import './assets/icons/sallaicons/style.scss'
import './assets/scss/index.scss'
import router from "./router";
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-bootstrap.css';
Vue.use(VueToast);
Vue.config.productionTip = false
import "@salla.sa/twilight-components/dist/twilight/twilight.esm.js"
import store from './store'

// Declare a global variable to hold the Twilight instance
Vue.prototype.$salla = window.salla || window.Salla || {};
Vue.prototype.mini = window.mini = {
  apiUrl: process.env.VUE_APP_API_URL || 'https://api.salla.dev/store/v1',
  checkoutURL: process.env.VUE_APP_CHECKOUT_URL,
  canMakePayments: false,
  appUrl: window.location.pathname
};

// Define a global object to hold the app state
const appState = Vue.observable({
  isTwilightReady: Vue.prototype.$salla.status === 'ready',
  isSummaryFetched: false,
  isOrderCompleted: false,
  hasError: false,
  disableCoupons: false,
  withLogout: false,
  inline: false,
  isInitialized: false // Track initialization to prevent duplicate init calls
});

Vue.prototype.$shouldRender = () => {
  return appState.isTwilightReady && appState.isSummaryFetched;
}
// Make the app state available to all components
Vue.prototype.$appState = appState;

function postEvent(name, payload) {
  window.parent.postMessage(
    {
      source: "checkout",
      message: { name, payload },
      type: 'dispatchEvent',
    },
    "*"
  );
}

// Listen for the message event to detect Twilight readiness
if (!appState.isTwilightReady) {
  window.addEventListener('message', (event) => {
    if (event.data.source !== 'mini-checkout') {
      return;
    }
    /** @type {{id?:Number,products?:Number[]|string}} */
    let data = event.data.data;
    let action = event.data.type;
    if (action === 'width') {
      return Vue.prototype.$parentWidth = data;
    }

    if (action == 'recalculate') {
      store.dispatch('recalculateHandler');
    }

    if (action === 'formDataValidation') {
      store.dispatch('validateFormData', data);
    }

    if (action !== 'init') {
      return;
    }

    // Prevent duplicate initialization
    if (appState.isInitialized) {
      console.log('Checkout already initialized, skipping duplicate init');
      return;
    }
    appState.isInitialized = true;

    Vue.prototype.mini.canMakePayments = data.canMakePayments;
    Vue.prototype.mini.appUrl = window.mini.appUrl = data.appUrl;
    if (data.apiUrl) {
      Vue.prototype.mini.apiUrl = window.mini.apiUrl = data.apiUrl;
    }
    if (data.inline) {
      document.documentElement.classList.add('inline');
      appState.inline = true
    }
    appState.hasFormSelector = !!data.hasFormSelector;
    //todo:: if data.products not existed or not there, show error message, that products not existed
    //todo:: fix issue on user request
    if (data.id) {
      salla.storage.set('Store-Identifier', data.id);
    }

    // Default colors
    const defaultColors = {
      'primary': '#baf3e6',
      'reverse-primary': '#004956',
      'reverse-text': '#004956',
      'text': '#444444',
    };

    // Inject default colors as global CSS variables
    Object.entries(defaultColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--color-${key}`, value);
    });

    // Check if custom colors are provided in data.config.colors
    if (data.config.colors) {
      // Setting the passed colors as global CSS variables
      Object.entries(data.config.colors).forEach(([key, value]) => {
        key = key.replace(/_/g, '-');
        // Remove the color if it exists
        if (document.documentElement.style.getPropertyValue(`--color-${key}`)) {
          document.documentElement.style.removeProperty(`--color-${key}`);
        }
        document.documentElement.style.setProperty(`--color-${key}`, value);
      });
    }


    // check if the passed config has headers, if so, we will override the default headers
    if (data.config.headers && typeof data.config.headers === 'object') {
      Object.entries(data.config.headers).forEach(([key, value]) => {
        const capitalizedKey = key.toUpperCase();

        // Remove the header if it exists
        delete salla.api.axios.defaults.headers.common[capitalizedKey];

        // Set the header with the new value
        salla.api.setHeader(key, value);
      });
    }
    if (data.config.disable_coupons) {
      appState.disableCoupons = true;
    }   
    
    if (data.config.with_logout) {
      appState.withLogout = true;
    }
    //lets initiate Salla
    let headers = salla.api.getHeaders();
    headers['S-APP-OS'] = 'fast-checkout';
    if (process.env.VUE_APP_CF_CLIENT) {
      headers["Cf-Access-Client-Id"] = process.env.VUE_APP_CF_CLIENT;
      headers["Cf-Access-Client-Secret"] = process.env.VUE_APP_CF_SECRET;
    }
    let token = (data.token || salla.storage.get('token') || '').replace(/"/g, '');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      localStorage.setItem("token", token)
      //let's send the token to the parent incase the user is logged in in the checkout
      window.parent.postMessage(
        {
          source: "checkout",
          message: token,
          type: "tokenFetched",
        },
        "*"
      );
    }
    // this condition is to avoid duplicated requests on re-opening the checkout modal
    if (appState.isSummaryFetched) {
      console.log('Summary already fetched, skipping duplicate fetch');
      return;
    }
    //todo:: handle error responses
    setTimeout(async () => {
      let config = await fetch(`${window.mini.apiUrl}/store/settings`, {
        headers: {
          "accept-language": data.lang || 'ar',
          ...headers,
          "Store-Identifier": data.id || process.env.VUE_APP_STORE_ID,
        },
      }).catch((e) => {
        console.error(e);
        appState.hasError = true;
      });
      if (!config) {
        return;
      }
      let settings = (await config.json()).data;

      // Check email and mobile settings from passed config and override the default settings
      let email = data.config?.auth?.email;
      let mobile = data.config?.auth?.mobile;
      email = email === undefined ? 'auto' : email;
      mobile = mobile === undefined ? 'auto' : mobile;
      if (email !== 'auto') {
        settings.store.settings.auth.email_allowed = email;
      }
      if (mobile !== 'auto') {
        settings.store.settings.auth.mobile_allowed = mobile;
      }

      // get user from the passed config and set it in the storage
      let user = data.config.user || Salla.storage.get('user');
      if(user?.id){
        //this is just workaround to avoid unnecessary requests, until we upgrade twilight, it's fixed after current version
        user.id = `${user.id}`; 
      }
      if (user && user.type === 'user') {
        window.salla.storage.set('user', user);
        settings.user = user;
      } else if (token) {
        let userResp = await fetch(`${window.mini.apiUrl}/auth/user`, {
          headers: {
            ...headers,
            "Store-Identifier": data.id || process.env.VUE_APP_STORE_ID,
          },
        })
        let fetchedUser = (await userResp.json()).data;
        fetchedUser.type = 'user';

        const { currency, language, phone, ...rest } = fetchedUser;
        const mappedUser = {
          ...settings.user,
          ...rest,
          currency_code: currency,
          language_code: language,
          mobile: phone ? `${phone.code?.replace('+', '')}${phone.number}` : null

        };
        window.salla.storage.set('user', mappedUser);
        settings.user = mappedUser;
      }

      appState.payments = data?.config?.payments && data?.config?.payments?.length ? data.config.payments : undefined;

      // init twilight
      window.salla.api.cart.latest = () => {
        console.info('Will create cart mini-checkout, so avoid getting latest cart from twilight!')
        return { data: { cart: { id: null } } }
      };
      await window.salla.init(settings);
      //todo:: remove this line, it's just to force showing applyPay
      salla.config.set('store.settings.is_salla_gateway', true);
      window.salla.lang.onLoaded(() => {
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.shipping_address', 'عنوان الشحن');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.shipping_address', 'Shipping Address');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.shipping_company', 'Shipper');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.new_address', 'عنوان جديد');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.new_address', 'New address');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.invoice_details', 'تفاصيل الفاتورة');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.invoice_details', 'Invoice Details');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.no_addresses', 'لا توجد لديك عناوين شحن مسجلة...');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.no_addresses', 'You have no shipping addresses...');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.no_companies', 'لا توجد شركات شحن متاحة، يرجى اختيار عنوان اخر');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.no_companies', 'There are no shipping companies available.');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.exit_after', 'الخروج تلقائيا بعد');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.exit_after', 'Exit after');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.unavailable_product', 'المنتج الذي تم اختياره غير متاح');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.unavailable_product', 'The selected product is not available');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.support_and_cs', 'خدمة العملاء و الدعم الفني');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.support_and_cs', 'Customer Service and Technical Support');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.empty_results', 'لا توجد نتائج');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.empty_results', 'No results');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.more_ways', 'طرق دفع أخرى');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.more_ways', 'Other payment methods');
        // Address form translations
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.address_saved', 'تم حفظ العنوان بنجاح');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.address_saved', 'Address saved successfully');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.address_updated', 'تم تحديث العنوان بنجاح');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.address_updated', 'Address updated successfully');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.add_new_address', 'إضافة عنوان جديد');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.add_new_address', 'Add new address');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.confirm_address', 'تأكيد العنوان');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.confirm_address', 'Confirm address');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.select_country', 'اختر الدولة');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.select_country', 'Select country');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.select_region', 'اختر المنطقة');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.select_region', 'Select region');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.select_city', 'اختر المدينة');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.select_city', 'Select city');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'pages.checkout.neighborhood', 'الحي');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'pages.checkout.neighborhood', 'Neighborhood');
        salla.helpers.setNested(salla.lang.messages['ar.trans'], 'common.errors.error_occurred', 'حدث خطأ غير متوقع');
        salla.helpers.setNested(salla.lang.messages['en.trans'], 'common.errors.error_occurred', 'An unexpected error occurred');
        document.title = salla.lang.get('pages.orders.finish_payment');
      });
      appState.isTwilightReady = true;
      window.parent.postMessage(
        {
          source: "checkout",
          message: salla.config.get("theme.is_rtl", true),
          type: "direction",
        },
        "*"
      );
      window.salla.notify.setNotifier(function () {
        return
      });

      if (window.salla.config.isGuest()) {
        localStorage.removeItem("token");
        window.parent.postMessage({source: "checkout", message: null, type: "removeToken"}, "*");
        window.parent.postMessage({source: "checkout", message: settings, type: "login"}, "*");
        postEvent('mini-checkout::initiated', settings);
        return;

      }
      let formData = data.formData;
      let cartId = data.cartId;
      if (!formData && !cartId) {
        salla.logger.error('failed to parse generate checkout, there are no products or cart-id or valid formSelector are passed!');
        return;
      }

      if (data.config.success_url) {
        formData.success_url = data.config.success_url;
      }

      if (data.config.failure_url) {
        formData.failure_url = data.config.failure_url;
      }

      try {
        let response = cartId
          ? salla.api.request(`cart/${cartId}/status`)
          : salla.api.request('checkout/mini', formData, 'post');
        response.then(res => {
          let nextStep = res.data.next_step || res.data;
          if (nextStep.to !== 'checkout') {
            throw "Unexpected response!";
          }
          Vue.prototype.mini.checkoutURL = window.mini.checkoutURL = cleanBaseUrl(nextStep.url);
          store.dispatch('summaryHandler').then((res) => {

            if (res && res.redirect) {
              window.parent.postMessage(
                {
                  source: "checkout",
                  message: res.redirect.url,
                  type: "redirect",
                },
                "*"
              );
            }
            store.dispatch('addressesHandler').then((res) => {
              appState.isSummaryFetched = true;
            });
            postEvent('mini-checkout::initiated', res);

          }).catch((e) => {
            appState.hasError = true;
            let errorCode = e.response?.status;
            let message = errorCode === 403
              ? e.response?.data?.message
              : salla.lang.get('pages.checkout.error_occurred');

            if (router.currentRoute.name !== 'error') {
              router.push({ name: 'error', params: { message } });
            }
            postEvent('mini-checkout::init-failed', salla.lang.get('pages.checkout.error_occurred'));
          });
        }).catch(error => {
          appState.hasError = true;
          let tokenError = error.response?.data?.error?.code === 'token_invalid';
          let errorCode = error.response?.data?.status;
          if (tokenError || errorCode === 401) {
            localStorage.removeItem("token");
            window.parent.postMessage(
              {
                source: "checkout",
                message: null,
                type: "removeToken",
              },
              "*"
            );
          }

          if (router.currentRoute.name !== 'error') {
            const message = errorCode === 422 || errorCode === 410
              ? salla.lang.get('pages.checkout.unavailable_product')
              : error.response?.data?.message || salla.lang.get('pages.checkout.error_occurred');
            if (errorCode === 422 || errorCode === 410) {
              postEvent('mini-checkout::product-unavailable', error.response?.data.error.message || message);
              window.parent.postMessage({ source: "checkout", type: "close" }, "*");
              return;
            }

            const desc = error.response?.data?.error?.message == 'alert.invalid_fields' ? '' : error.response?.data?.error?.message;
            router.push({ name: 'error', params: { message, desc } });
            postEvent('mini-checkout::init-failed', message);
          }
        });
      } catch (e) {
        salla.logger.error('failed to parse generate checkout!', formData, cartId, e);
      }
    }, 200);
  });
}



new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
